import React, { useEffect } from 'react';
import cn from 'classnames';
import _ from 'lodash';
import { useRecoilState } from 'recoil';
import {
  UserLocation,
  UserLocationState,
  getFinanceFormInputPersonalised,
} from '~/common/store';
import { usePortals } from 'react-portal-hook';
import { GfvDisclaimer, PostcodeModal } from '~/common/components';
import { CompareRangePriceDetail } from '~/pages/model-landing/components/model-landing-price-info';
import { ModelEntry } from '../../types';
import styles from './index.module.scss';
import { Button } from '~/common/components/ui-elements';

interface VehicleModelProps {
  model: ModelEntry;
  className?: string;
}
export const VehicleModel = React.memo<VehicleModelProps>(
  ({ model, className }) => {
    const portalManager = usePortals();
    const [locationState, setLocationState] = useRecoilState(UserLocationState);

    const openPostcodeModal = () => {
      portalManager.open((portal) => (
        <PostcodeModal closeModal={portal.close} />
      ));
    };

    useEffect(() => {
      const onDealerChanged = (e: CustomEvent<{ suburb?: UserLocation }>) => {
        if (e.detail?.suburb && !_.isEqual(e.detail.suburb, locationState)) {
          setLocationState(e.detail.suburb);
        }
      };
      window.addEventListener(
        'dealerChanged',
        onDealerChanged as EventListener
      );
      return () => {
        window.removeEventListener(
          'dealerChanged',
          onDealerChanged as EventListener
        );
      };
    }, [locationState, setLocationState]);

    const isHomepage =
      typeof window !== 'undefined' && window.location.pathname === '/';
    const hideGFV = isHomepage && model.hideGfvHomepage;

    return (
      <div className={cn(styles.vehicleModel, className)}>
        <img
          alt={model.modelName}
          src={model.variantImage}
          className={styles.variantImage}
          loading="lazy"
        />
        <h2 className={styles.modelName}>{model.modelName}</h2>
        <div
          className={styles.modelBlurb}
          dangerouslySetInnerHTML={{ __html: model.modelBlurb }}
        />
        <p className={styles.variantName}>{model.variantName} shown</p>

        {model.displayVariantPrice && (
          <>
            <CompareRangePriceDetail
              type="RDP"
              variantId={model.variantId}
              modelName={model.variantName}
              pimIdentifier={model.variantPimIdentifier}
              isFinanceFormInputPersonalised={getFinanceFormInputPersonalised()}
              containerClass={
                hideGFV
                  ? `${styles.priceDetailsContainer} ${styles.withBottomBorder}`
                  : styles.priceDetailsContainer
              }
              labelClass={styles.priceLabelClass}
              priceClass={styles.priceClass}
              recommendedRdpLabelClass={styles.recommendedRdpLabelClass}
              recommendedRdpLabel="Recommended Driveaway Price"
              additionalOptionsPrice={model.additionalOptionsPrice}
              rdpDisclaimerOverride={model.rdpDisclaimerOverride}
            />

            {!hideGFV && (
              <CompareRangePriceDetail
                type="Finance"
                variantId={model.variantId}
                pimIdentifier={model.variantPimIdentifier}
                isFinanceFormInputPersonalised={getFinanceFormInputPersonalised()}
                containerClass={styles.financeContainer}
                labelClass={styles.priceLabelClass}
                priceClass={styles.priceClass}
                additionalOptionsPrice={model.additionalOptionsPrice}
              />
            )}

            <div className={styles.pricingDisclaimer}>
              <p className={styles.rdpDisclaimer}>
                Recommended Driveaway Price (RDP) shown based on{' '}
                {`${locationState.name}, ${locationState.postcode}`}. RDP may
                change based on location.{' '}
                <span
                  onClick={openPostcodeModal}
                  className={styles.postcodeModal}
                >
                  Enter your postcode
                </span>{' '}
                for the RDP in your area.
              </p>

              <GfvDisclaimer variantPimId={model.variantPimIdentifier} />
            </div>
          </>
        )}

        <div className={styles.ctaButtons}>
          {!model.explore?.ctaHidden && (
            <Button
              type="primary"
              buttonSize="large"
              buttonWidth="full-width"
              target={model.explore?.ctaUrl?.target || '_self'}
              href={
                model.explore?.ctaUrl?.url ||
                `/${model.modelName.toLowerCase()}`
              }
              className={styles.fullSpecsFeatButton}
            >
              {model.explore?.ctaUrl?.name || `Explore ${model.modelName}`}
            </Button>
          )}
          {!model?.buildYourOwn?.ctaHidden && (
            <Button
              type="bordered"
              buttonSize="large"
              buttonWidth="full-width"
              href={model.buildYourOwn?.ctaUrl?.url || '/configure'}
            >
              {model.buildYourOwn?.ctaUrl?.name || `Build your own`}
            </Button>
          )}
        </div>
      </div>
    );
  }
);
